import { create } from 'zustand';
import { SIM, type Platform } from '../../shared/config';
import { DEFAULT_TRACKED } from '../../shared/kols';
import { load, save } from '../lib/storage';

export interface ColumnFilter { search: string; platforms: Platform[]; minMc: number; maxMc: number; hideNoSocials: boolean; quickBuySol: number | null }
export interface Settings {
  feedSide: 'left' | 'right'; feedWidth: number; feedOpen: boolean; trackedOpen: boolean; trackedWidth: number; rightWidth: number; chartSplit: number; rightOpen: boolean; slippagePct: number;
  /** explicit quick-buy values the user typed; null = inherit global */
  trackedQuickBuySol: number | null; qbCustom: boolean;
  feedFilters: { trackedOnly: boolean; replies: boolean; quotes: boolean };
  markerFilter: { you: boolean; kol: boolean; peer: boolean; dev: boolean };
  indicators: { sma: boolean; ema: boolean };
  tradePresets: { buy: number[]; sell: number[] }[]; tradePage: number;
  quickBuyOn: boolean; quickBuySol: number; buyPresets: number[]; sellPresets: number[];
  instant: { x: number; y: number; open: boolean; page: number; pages: { buy: number[]; sell: number[] }[] };
  latencyMs: number; sound: boolean; reducedMotion: boolean; assistedAudio: boolean; compactCards: boolean; showSocials: boolean;
  columns: Record<'new' | 'final' | 'migrated', ColumnFilter>;
  hidden: string[]; tracked: string[]; keywords: string[];
  /** feed accounts to highlight (independent from catalyst eligibility) */
  trackedAccounts: string[];
  launcher: { autoTicker: boolean; autofillHighlighted: boolean; autoDeploy: boolean; openAfter: boolean; devPresets: number[]; bundleCount: number; bundleSol: number; vanity: boolean; deployKey: string };
  chartUnit: 'SOL' | 'USD'; chartMode: 'price' | 'mc'; chartLog: boolean; chartTf: number;
  playerName: string;
}
const col = (): ColumnFilter => ({ search: '', platforms: [], minMc: 0, maxMc: 0, hideNoSocials: false, quickBuySol: null });
export const DEFAULTS: Settings = {
  feedSide: 'left', feedWidth: 290, feedOpen: true, trackedOpen: true, trackedWidth: 240, rightWidth: 290, chartSplit: 0.68, rightOpen: true, slippagePct: 20,
  trackedQuickBuySol: null, qbCustom: false,
  feedFilters: { trackedOnly: false, replies: true, quotes: true },
  markerFilter: { you: true, kol: true, peer: true, dev: true },
  indicators: { sma: false, ema: false },
  tradePresets: [{ buy: [0.1, 0.25, 0.5, 1, 2, 5, 0.05, 10], sell: [10, 25, 50, 100] }, { buy: [0.05, 0.1, 0.2, 0.3, 0.5, 0.75, 1, 1.5], sell: [5, 10, 33, 100] }, { buy: [1, 2, 3, 5, 7.5, 10, 15, 20], sell: [25, 50, 75, 100] }], tradePage: 0,
  quickBuyOn: true, quickBuySol: SIM.QUICK_BUY_SOL, buyPresets: [0.1, 0.25, 0.5, 1, 2, 5], sellPresets: [25, 50, 100],
  instant: { x: Math.max(80, (typeof window !== 'undefined' ? window.innerWidth : 1600) - 640), y: 160, open: false, page: 0, pages: [{ buy: [0.1, 0.25, 0.5, 1, 2, 5, 10, 0.05], sell: [10, 25, 50, 100, 1, 3, 33, 75] }, { buy: [0.05, 0.1, 0.2, 0.3, 0.5, 0.75, 1, 1.5], sell: [5, 10, 33, 100, 15, 20, 40, 60] }, { buy: [1, 2, 3, 5, 7.5, 10, 15, 20], sell: [25, 50, 75, 100, 10, 20, 30, 90] }] },
  latencyMs: SIM.DEFAULT_LATENCY_MS, sound: true, reducedMotion: false, assistedAudio: false, compactCards: false, showSocials: true,
  columns: { new: col(), final: col(), migrated: col() },
  hidden: [], tracked: DEFAULT_TRACKED, keywords: [], trackedAccounts: ['sim-elon', 'sim-trump'],
  launcher: { autoTicker: true, autofillHighlighted: false, autoDeploy: false, openAfter: false, devPresets: [0.5, 1, 2, 5], bundleCount: 0, bundleSol: 1, vanity: false, deployKey: 'Enter' },
  chartUnit: 'USD', chartMode: 'mc', chartLog: false, chartTf: 1, // handoff: MarketCap / USD / 1s / linear
  playerName: '',
};

interface S extends Settings { settingsAt: number; set: (p: Partial<Settings>) => void; replace: (s: Partial<Settings>, at: number) => void; reset: () => void }
const stored = migrate(load<{ s: Partial<Settings>; at: number; v?: number }>('settings', { s: {}, at: 0 }));
/** v2: quick buy defaults to 0. An untouched old default (100, never customised) migrates to 0; custom values stay. */
function migrate(x: { s: Partial<Settings>; at: number; v?: number }) {
  if ((x.v ?? 1) < 2) {
    if (x.s.quickBuySol === 100 && !x.s.qbCustom) x.s.quickBuySol = 0;
    if (x.s.tracked && x.s.tracked.some((t) => !t.startsWith('kol-'))) x.s.tracked = DEFAULT_TRACKED; // old parody handles
    if (x.s.feedSide === 'right' && x.s.feedWidth === 380) { x.s.feedSide = 'left'; x.s.feedWidth = 290; }
    x.v = 2;
  }
  if ((x.v ?? 2) < 3) {
    if (x.s.quickBuySol === 0 && !x.s.qbCustom) x.s.quickBuySol = 0.5;
    if (x.s.buyPresets && Math.max(...x.s.buyPresets) > 50) delete x.s.buyPresets;
    if (x.s.tradePresets && Math.max(...x.s.tradePresets.flatMap((p) => p.buy)) > 100) delete x.s.tradePresets;
    x.v = 3;
  }
  return x;
}
export const useSettings = create<S>((set, get) => ({
  ...DEFAULTS, ...stored.s, settingsAt: stored.at,
  set: (p) => { set({ ...p, settingsAt: Date.now() }); persist(get()); },
  replace: (s, at) => { set({ ...DEFAULTS, ...s, settingsAt: at }); persist(get()); },
  reset: () => { set({ ...DEFAULTS, settingsAt: Date.now() }); persist(get()); },
}));
export function settingsSnapshot(s: S = useSettings.getState()): Settings {
  const out: Record<string, unknown> = {}; for (const k of Object.keys(DEFAULTS)) out[k] = (s as unknown as Record<string, unknown>)[k];
  return out as unknown as Settings;
}
function persist(s: S) { save('settings', { s: settingsSnapshot(s), at: s.settingsAt, v: 3 }); }
